/**
 * The ONLY `useBlocker` of the app (react-router uses the last one registered and warns about
 * two). Mounted once in `AppShell`, inside `UnsavedChangesProvider`. Renders nothing.
 */

import { useEffect } from 'react'

import { useBlocker, useLocation } from 'react-router-dom'

import { useUnsavedChangesRegistry } from './UnsavedChangesProvider'

type Registry = NonNullable<ReturnType<typeof useUnsavedChangesRegistry>>

function ArmedBlocker({ registry }: { registry: Registry }) {
  const location = useLocation()
  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    registry.shouldBlock(currentLocation, nextLocation),
  )

  useEffect(() => {
    if (blocker.state !== 'blocked') return
    registry.requestLeave({
      proceed: () => blocker.proceed(),
      cancel: () => blocker.reset(),
      current: { pathname: location.pathname, search: location.search },
      next: { pathname: blocker.location.pathname, search: blocker.location.search },
      to: blocker.location.pathname,
    })
    // `location` is read at the moment of blocking only; it must not re-fire the request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocker, registry])

  return null
}

/**
 * Without a provider (a test mounted on a `MemoryRouter`, which has no data router and would make
 * `useBlocker` throw) nothing is mounted.
 */
export function UnsavedChangesNavigationBlocker() {
  const registry = useUnsavedChangesRegistry()
  return registry ? <ArmedBlocker registry={registry} /> : null
}
